'use client'

import { twMerge } from '@/lib/classname/twMerge'
import { Button } from '@/ui/buttons/button/Button.client'
import { CloseButton } from '@/ui/buttons/close-button/CloseButton.client'
import { Icon } from '@/ui/icons/icon/Icon'
import { BaseSkeleton } from '@/ui/skeleton/BaseSkeleton'
import type { ReactNode } from 'react'
import { Heading } from 'react-aria-components'

type DrawerHeaderProps = {
  title?: string
  subtitle?: string
  isTitleUppercase?: boolean
  isLoading?: boolean
  hideCloseButton?: boolean
  isPending: boolean
  heading: ReactNode
  isBodyScrolledDown: boolean
  onBack?: () => void
}

export function DrawerHeader({
  title,
  subtitle,
  isTitleUppercase = false,
  isLoading,
  hideCloseButton = false,
  isPending,
  heading,
  isBodyScrolledDown,
  onBack,
}: DrawerHeaderProps) {
  return (
    <header
      className={twMerge(
        // -mx-3 px-3: full-bleed scroll shadow, content stays aligned with Skin px-3 — forced to because of ScrollableY (has its own -mx-1/p-1)
        'relative z-base -mx-3 shrink-0 px-3 py-3 transition-shadow duration-300',
        'embed:pt-5',
        // FIXME: no shadow-s rest-state token (6 layers @ alpha 0) — shadow-none won't animate to shadow-s; custom until box-shadow tokens rework
        isBodyScrolledDown
          ? 'shadow-s'
          : 'shadow-[0_0_0.25rem_-0.0625rem_rgba(32,38,51,0)_inset,0_1.5rem_1.5rem_-0.75rem_rgba(32,38,51,0),0_0.75rem_0.75rem_-0.375rem_rgba(32,38,51,0),0_0.375rem_0.375rem_-0.1875rem_rgba(32,38,51,0),0_0.1875rem_0.1875rem_-0.09375rem_rgba(32,38,51,0),0_0.0625rem_0.0625rem_-0.03125rem_rgba(32,38,51,0)]',
      )}
    >
      <div className="flex items-start gap-3">
        {onBack && (
          <Button
            variant="quaternary"
            isRound
            size="md"
            iconLeft={<Icon.ArrowLeft />}
            onPress={onBack}
            isDisabled={isPending}
            className="shrink-0"
          />
        )}
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          {isLoading ? (
            <>
              {title && (
                <Heading slot="title" className="sr-only">
                  {title}
                </Heading>
              )}
              <BaseSkeleton className="h-3 w-[16.25rem] max-w-full rounded-sm" aria-hidden />
              <BaseSkeleton className="h-2 w-[6.25rem] max-w-full rounded-full" aria-hidden />
            </>
          ) : (
            <>
              {title && (
                <Heading
                  slot="title"
                  data-testid="drawer/title"
                  className={twMerge(
                    'line-clamp-2 text-3xl font-bold leading-light text-brand-2-900',
                    isTitleUppercase && 'uppercase',
                  )}
                >
                  {title}
                </Heading>
              )}
              {subtitle && (
                <p
                  className="text-neutral-700 text-lg font-medium leading-light"
                  data-testid="drawer/subtitle"
                >
                  {subtitle}
                </p>
              )}
            </>
          )}
        </div>
        {!hideCloseButton && (
          <CloseButton
            slot="close"
            variant="secondary"
            size="md"
            isDisabled={isPending}
            className="shrink-0 embed:hidden"
          />
        )}
      </div>
      {heading && <div className="mt-3 min-w-0">{heading}</div>}
    </header>
  )
}
